
import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Target, Menu, Check, X, Zap } from 'lucide-react';
import { PROJECTS } from '../constants';
import { Section } from '../App';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface UIProps {
  currentProject: number;
  activeSection: Section;
  progress: number;
  onNavigate: (section: Section) => void;
  heroRef: React.RefObject<HTMLDivElement | null>;
  worksRef: React.RefObject<HTMLDivElement | null>;
  solutionsRef: React.RefObject<HTMLDivElement | null>;
  studioRef: React.RefObject<HTMLDivElement | null>;
  contactRef: React.RefObject<HTMLDivElement | null>;
}

export const UI: React.FC<UIProps> = ({ 
  currentProject, 
  activeSection, 
  progress, 
  onNavigate,
  heroRef,
  worksRef,
  solutionsRef,
  studioRef,
  contactRef
}) => {
  const project = PROJECTS[currentProject];
  const navRef = useRef<HTMLElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Handle Mobile Menu Animations
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      const tl = gsap.timeline();
      tl.to(mobileMenuRef.current, { 
        display: 'flex',
        opacity: 1, 
        duration: 0.4, 
        ease: "power2.out" 
      })
      .fromTo(".menu-header", 
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "expo.out" }
      )
      .fromTo(".mobile-nav-item", 
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.05, duration: 0.6, ease: "expo.out" },
        "-=0.3"
      )
      .fromTo(".menu-footer", 
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 },
        "-=0.4"
      );
    } else {
      document.body.style.overflow = 'auto';
      gsap.to(mobileMenuRef.current, { 
        opacity: 0, 
        duration: 0.3, 
        ease: "power2.in",
        onComplete: () => {
          if (mobileMenuRef.current) mobileMenuRef.current.style.display = 'none';
        }
      });
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });
      gsap.set(".hero-char-node", { opacity: 0, scale: 0.85, rotateX: -30, z: -30 });

      tl.to(".hero-char-node", { opacity: 1, scale: 1, rotateX: 0, z: 0, stagger: { each: 0.04, from: "center" }, duration: 1.8, ease: "power4.out" }, 0.6);
      tl.from(".hero-ui-accent", { opacity: 0, y: 10, duration: 1.2 }, 1.5);

      // Fade out hero content as user scrolls down to avoid overlapping with gallery
      if (heroRef.current && heroContentRef.current) {
        gsap.to(heroContentRef.current, {
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom center",
            scrub: true,
          },
          opacity: 0,
          y: -50,
          scale: 0.95,
          pointerEvents: "none"
        });
      }

      if (solutionsRef.current) {
        gsap.from(".solution-card-item", {
          scrollTrigger: { trigger: solutionsRef.current, start: "top 70%", end: "center center", toggleActions: "play none none reverse" },
          y: 40, opacity: 0, stagger: 0.1, duration: 1, ease: "power3.out"
        });
      }

      if (contactRef.current) {
        const contactTl = gsap.timeline({
          scrollTrigger: {
            trigger: contactRef.current,
            start: "top 80%",
            end: "bottom bottom",
            toggleActions: "play none none reverse"
          }
        });

        contactTl.from(".contact-header", { y: 100, opacity: 0, skewY: 5, duration: 1.2, ease: "expo.out" })
                 .from(".contact-line", { scaleX: 0, transformOrigin: "left", duration: 1, ease: "power4.inOut" }, "-=0.8");
      }
    });

    return () => ctx.revert();
  }, [heroRef, solutionsRef, contactRef]);

  useEffect(() => {
    const sections = ['WORKS', 'SOLUTIONS', 'STUDIO', 'CONTACT'];
    sections.forEach(section => {
      const btn = document.querySelector(`[data-section="${section}"]`);
      if (btn) {
        const bit = btn.querySelector('.nav-bit');
        const isActive = activeSection === section;
        
        if (isActive) {
           gsap.to(btn, { color: '#ffffff', x: 2, duration: 0.3, ease: "power2.out", overwrite: 'auto' });
           gsap.to(bit, { opacity: 1, x: -3, duration: 0.3, overwrite: 'auto' });
        } else {
           gsap.to(btn, { color: 'rgba(255,255,255,0.2)', x: 0, duration: 0.3, ease: "power2.out", overwrite: 'auto' });
           gsap.to(bit, { opacity: 0, x: 0, duration: 0.3, overwrite: 'auto' });
        }
      }
    });
  }, [activeSection]);

  const handleCharInteraction = (e: React.MouseEvent<HTMLSpanElement>, active: boolean) => {
    const el = e.currentTarget;
    const main = el.querySelector('.char-layer-main');
    const ghost = el.querySelector('.char-layer-ghost');
    if (!main || !ghost) return;

    if (active) {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - (rect.left + rect.width / 2);
      const y = e.clientY - (rect.top + rect.height / 2);
      gsap.to(main, { x: x * 0.3, y: y * 0.3, rotationY: x * 0.2, rotationX: -y * 0.2, scale: 1.08, z: 40, duration: 0.4, ease: "power2.out", overwrite: true });
      gsap.to(ghost, { x: x * 0.1, y: y * 0.1, opacity: 0.6, z: -10, duration: 0.6, ease: "power2.out", overwrite: true });
    } else {
      gsap.to(main, { x: 0, y: 0, rotationY: 0, rotationX: 0, scale: 1, z: 0, duration: 1, ease: "elastic.out(1, 0.6)", overwrite: true });
      gsap.to(ghost, { x: 0, y: 0, opacity: 0, duration: 0.8, ease: "power2.out", overwrite: true });
    }
  };

  const handleCardHover = (e: React.MouseEvent<HTMLDivElement>, isRecommended: boolean = false) => {
    const card = e.currentTarget;
    gsap.to(card, {
      y: -5,
      scale: isRecommended ? 1.05 : 1.02,
      zIndex: 50,
      borderColor: 'rgba(136, 136, 255, 0.4)',
      backgroundColor: 'rgba(136, 136, 255, 0.03)',
      duration: 0.4,
      ease: "power2.out",
      overwrite: "auto"
    });
  };

  const handleCardReset = (e: React.MouseEvent<HTMLDivElement>, isRecommended: boolean = false) => {
    const card = e.currentTarget;
    gsap.to(card, {
      y: 0,
      scale: isRecommended ? 1.03 : 1.0,
      zIndex: isRecommended ? 10 : 1,
      borderColor: isRecommended ? 'rgba(136, 136, 255, 0.3)' : 'rgba(255, 255, 255, 0.05)',
      backgroundColor: isRecommended ? 'rgba(136, 136, 255, 0.02)' : 'rgba(255, 255, 255, 0.01)',
      duration: 0.8,
      ease: "power3.out",
      overwrite: "auto"
    });
  };

  const handleNavHover = (e: React.MouseEvent<HTMLButtonElement>, active: boolean) => {
    const target = e.currentTarget;
    const bit = target.querySelector('.nav-bit');
    const underline = target.querySelector('.nav-underline');
    
    if (active) {
      gsap.to(target, { color: '#ffffff', x: 2, duration: 0.3, ease: "power2.out", overwrite: 'auto' });
      gsap.to(bit, { opacity: 1, x: -3, duration: 0.3, overwrite: 'auto' });
      gsap.to(underline, { width: '100%', duration: 0.3, ease: "power2.out", overwrite: 'auto' });
    } else {
      const section = target.getAttribute('data-section');
      const isActuallyActive = activeSection === section;
      
      gsap.to(target, { 
        color: isActuallyActive ? '#ffffff' : 'rgba(255,255,255,0.2)', 
        x: isActuallyActive ? 2 : 0, 
        duration: 0.3, 
        overwrite: 'auto' 
      });
      gsap.to(bit, { 
        opacity: isActuallyActive ? 1 : 0, 
        x: isActuallyActive ? -3 : 0, 
        duration: 0.3, 
        overwrite: 'auto' 
      });
      gsap.to(underline, { width: '0%', duration: 0.3, overwrite: 'auto' });
    }
  };

  const handleScrollTo = (section: Section) => {
    setIsMenuOpen(false);
    if (section === 'HERO' && heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (section === 'WORKS' && worksRef.current) {
      worksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (section === 'SOLUTIONS' && solutionsRef.current) {
      solutionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (section === 'STUDIO' && studioRef.current) {
      studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (section === 'CONTACT' && contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderKryven = () => {
    const accentColor = '#8888ff'; 
    return "KRYVEN".split("").map((char, i) => {
      const isPurple = i >= 3; 
      const brightness = isPurple ? 1.0 : (i < 2 ? 1.2 : 1.3);
      const shadow = isPurple 
        ? `0 0 40px rgba(136, 136, 255, 0.85), 0 0 80px rgba(136, 136, 255, 0.45), 0 0 10px rgba(255, 255, 255, 0.8)` 
        : `0 0 45px rgba(255, 255, 255, 0.55), 0 0 10px rgba(255, 255, 255, 0.9)`;
      
      return (
        <span key={i} className="char-cluster group spatial-scene" onMouseMove={(e) => handleCharInteraction(e, true)} onMouseLeave={(e) => handleCharInteraction(e, false)}>
          <span 
            className="hero-char-node char-layer-main text-white" 
            style={{ 
              textShadow: shadow,
              filter: `brightness(${brightness}) contrast(1.1)`, 
              WebkitTextStroke: '1px rgba(255,255,255,0.05)',
              opacity: isPurple ? 0.9 : 1.0
            }}
          >
            {char}
          </span>
          <span className="char-layer-ghost" style={{ color: accentColor }}>{char}</span>
        </span>
      );
    });
  };

  const gallerySectionProgress = Math.max(0, Math.min(1, (progress - 0.15) / 0.67));

  return (
    <div className="w-full">
      {/* REFINED MOBILE DROPDOWN MENU */}
      <div 
        ref={mobileMenuRef}
        className="fixed inset-0 z-[500] bg-[#050505]/95 backdrop-blur-[40px] hidden flex-col px-6 md:px-24 overflow-hidden items-center justify-center"
        style={{ opacity: 0 }}
      >
        <div className="menu-header absolute top-6 flex items-center gap-10 px-8 py-3.5 bg-white/5 border border-white/10 rounded-full w-auto max-w-full">
          <div className="flex items-center gap-3">
            <Target size={16} className="text-[#8888ff]" />
            <h1 className="text-[11px] font-black italic tracking-[-0.05em] uppercase leading-none text-white">KRYVEN_LAB</h1>
          </div>
          <button 
            onClick={() => setIsMenuOpen(false)}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-all group border border-white/5"
          >
            <X className="text-white group-hover:rotate-90 transition-transform duration-500" size={16} />
          </button>
        </div>

        <div className="relative flex flex-col gap-8 w-full max-w-2xl items-start text-left">
          {[
            { id: 'HERO', label: 'HOME' },
            { id: 'WORKS', label: 'WORK' },
            { id: 'SOLUTIONS', label: 'CAPABILITIES' },
            { id: 'STUDIO', label: 'INFRASTRUCTURE' },
            { id: 'CONTACT', label: 'CONTACT' }
          ].map((item, idx) => {
            const isActive = activeSection === item.id;
            return (
              <button 
                key={item.id}
                onClick={() => handleScrollTo(item.id as Section)}
                className="mobile-nav-item group flex flex-col items-start transition-all duration-300"
              >
                <h2 className={`text-[6.5vw] md:text-5xl font-black italic uppercase tracking-tighter transition-all duration-500 leading-none ${isActive ? 'text-white' : 'text-white/20 group-hover:text-white/60 group-hover:translate-y-[-4px]'}`}>
                  {item.label}
                </h2>
              </button>
            );
          })}
        </div>

        <div className="menu-footer absolute bottom-10 left-10 md:left-24 flex flex-col gap-2 opacity-30">
          <p className="mono text-[8px] text-white tracking-[0.4em] uppercase font-bold">ESTABLISHED_2025 // AESTHETIC_SYNC</p>
        </div>
        
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-white/5 pointer-events-none" />
        <div className="absolute top-0 left-1/2 w-[1px] h-full bg-white/5 pointer-events-none" />
      </div>

      <nav ref={navRef} className="fixed top-6 left-1/2 -translate-x-1/2 z-[400] pointer-events-none w-auto max-w-[95vw]">
        <div className="flex items-center gap-6 md:gap-10 px-6 py-3 md:px-8 md:py-3.5 bg-[#0a0a0a]/80 backdrop-blur-2xl border border-white/10 rounded-full pointer-events-auto shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all duration-500 hover:border-[#8888ff]/30">
          <div className="group cursor-pointer flex items-center gap-3 shrink-0" onClick={() => handleScrollTo('HERO')}>
            <div className="relative w-7 h-7 md:w-8 md:h-8 border border-white/10 rounded-full flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:border-[#8888ff]/40 bg-black/40">
              <Target size={14} className="text-white/40 group-hover:text-[#8888ff] group-hover:rotate-180 transition-all duration-700" />
            </div>
            <h1 className="text-[10px] md:text-[11px] font-black italic tracking-[-0.05em] uppercase leading-none text-white hidden sm:block">KRYVEN</h1>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {['WORKS', 'SOLUTIONS', 'STUDIO', 'CONTACT'].map((id, idx) => (
              <button 
                key={id} 
                data-section={id}
                onClick={() => handleScrollTo(id as Section)} 
                onMouseEnter={(e) => handleNavHover(e, true)} 
                onMouseLeave={(e) => handleNavHover(e, false)} 
                className="relative py-1 text-[9px] font-black italic tracking-[0.4em] uppercase mono transition-none flex items-center text-white/20"
              >
                <span className="nav-bit opacity-0 mono mr-1.5 text-[#8888ff] pointer-events-none">0{idx + 1}.</span>
                {id === 'STUDIO' ? 'PLANS' : id}
                <div className="nav-underline absolute -bottom-1 left-0 h-[1px] bg-[#8888ff] pointer-events-none" style={{ width: 0 }} />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 md:hidden sm:flex border-l border-white/10 pl-4">
            <div className="w-1.5 h-1.5 rounded-full bg-[#8888ff] animate-pulse" />
            <span className="mono text-[8px] tracking-[0.2em] uppercase opacity-40 font-bold">
               {activeSection === 'HERO' ? 'CORE' : activeSection}
            </span>
          </div>

          <button 
            onClick={() => setIsMenuOpen(true)}
            className="order-first md:order-last w-8 h-8 md:w-9 md:h-9 border border-white/10 rounded-full flex items-center justify-center hover:bg-white/5 transition-all group pointer-events-auto bg-black/20"
          >
             <Menu size={16} className="text-white/70 group-hover:rotate-90 transition-transform duration-500" />
          </button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-screen w-full flex flex-col items-center justify-center text-center px-4 md:px-24 overflow-hidden bg-transparent">
        <div ref={heroContentRef} className="relative z-10 w-full max-w-[1600px] mx-auto flex flex-col items-center translate-y-0 md:translate-y-[14vh]">
          <div className="relative inline-block perspective-[2000px] max-w-full z-20">
             <h2 className="relative text-[10vw] md:text-[13vw] font-black uppercase italic leading-[0.8] mb-12 select-none whitespace-nowrap spatial-scene flex justify-center gap-x-[0.01em] md:gap-x-[0.02em] px-4 md:px-12 drop-shadow-2xl">
                {renderKryven()}
             </h2>
          </div>
          <div className="hero-ui-accent flex flex-col items-center relative z-20 max-w-5xl">
             <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black italic tracking-tighter uppercase text-center text-white mb-8 drop-shadow-lg leading-tight px-4">
               WHERE AESTHETICS MEET <br className="hidden md:block" /> AUTONOMOUS COMMERCE
             </h3>
             <p className="text-sm sm:text-base md:text-lg text-white/60 font-light max-w-xl mx-auto mb-12 leading-relaxed tracking-wide px-8 text-center opacity-80">From boutique launches to enterprise infrastructure, Kryven translates brand identity into high-performance digital reality. We turn your vision into an automated revenue engine.</p>
          </div>
        </div>
        
        <div className="absolute top-[40%] left-1/2 -translate-x-1/2 w-[60vw] h-[40vh] bg-[#8888ff]/[0.02] blur-[120px] rounded-full pointer-events-none" />
      </section>

      {/* WORKS SECTION */}
      <section ref={worksRef} className="relative h-[1200vh] w-full z-20">
        <div className="sticky top-0 h-screen w-full flex flex-col justify-end pb-[8vh] md:pb-[6vh] overflow-hidden">
          <div className={`relative z-20 w-full flex flex-col items-start px-6 sm:px-12 md:px-24 transition-all duration-700 ${activeSection === 'WORKS' ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center gap-6 md:gap-8 mb-2 opacity-60">
               <span className="mono text-[10px] md:text-[11px] tracking-[0.5em] uppercase font-black text-[#8888ff] italic">INDEX.0{currentProject + 1}</span>
               <div className="h-[1px] w-16 md:w-48 bg-white/10 relative">
                  <div className="absolute top-0 left-0 h-full bg-[#8888ff] transition-all duration-300" style={{ width: `${Math.max(0, Math.min(1, gallerySectionProgress)) * 100}%` }} />
               </div>
            </div>
            {/* The project title text overlay was removed from here */}
          </div>
        </div>
      </section>

      {/* SOLUTIONS SECTION */}
      <section ref={solutionsRef} className="relative w-full z-30 bg-[#050505] py-20 md:py-40 border-t border-white/5">
         <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16 solution-card-item">
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-black italic uppercase text-white tracking-tighter px-4">Professionalism Pays Off</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 lg:gap-24 mb-20 md:mb-40">
                <div className="solution-card-item relative p-6 sm:p-8 border border-white/5 bg-white/[0.02] flex flex-col gap-6">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="mono text-red-500/80 tracking-widest text-[9px] md:text-[10px] uppercase">The Old Way (Inefficiency)</h3>
                        <X size={14} className="text-red-500/50" />
                    </div>
                    <ul className="space-y-4 md:space-y-6 mono text-[11px] md:text-[12px] text-white/40">
                        <li className="flex items-start gap-3">
                           <span className="text-white/20 mt-[2px]">-</span>
                           <span><strong className="text-white/60">High Friction:</strong> Fragmented communication and manual sales cycles that stall growth.</span>
                        </li>
                        <li className="flex items-start gap-3">
                           <span className="text-white/20 mt-[2px]">-</span>
                           <span><strong className="text-white/60">Legacy Performance:</strong> Outdated templates and monolithic codebases that kill user retention.</span>
                        </li>
                        <li className="flex items-start gap-3">
                           <span className="text-white/20 mt-[2px]">-</span>
                           <span><strong className="text-white/60">Visual Debt:</strong> Generic design that fails to communicate the true value of your brand.</span>
                        </li>
                    </ul>
                </div>
                <div className="solution-card-item relative p-6 sm:p-8 border border-[#8888ff]/20 bg-[#8888ff]/[0.03] flex flex-col gap-6 shadow-[0_0_50px_rgba(136,136,255,0.05)]">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="mono text-[#8888ff] tracking-widest text-[9px] md:text-[10px] uppercase">The Kryven Way</h3>
                        <Check size={14} className="text-[#8888ff]" />
                    </div>
                    <ul className="space-y-4 md:space-y-6 mono text-[11px] md:text-[12px] text-white">
                        <li className="flex items-start gap-3">
                           <Zap size={12} className="text-[#8888ff] shrink-0 mt-[2px]" />
                           <span><strong className="text-white">Autonomous Operations:</strong> Zero-touch checkout and automated workflows that scale with you.</span>
                        </li>
                        <li className="flex items-start gap-3">
                           <Zap size={12} className="text-[#8888ff] shrink-0 mt-[2px]" />
                           <span><strong className="text-white">Edge-Driven Speed:</strong> High-performance React architecture with sub-second global latency.</span>
                        </li>
                        <li className="flex items-start gap-3">
                           <Zap size={12} className="text-[#8888ff] shrink-0 mt-[2px]" />
                           <span><strong className="text-white">Signature Identity:</strong> Bespoke UI/UX and advanced animations that command market authority.</span>
                        </li>
                    </ul>
                </div>
            </div>
         </div>
      </section>

      {/* STUDIO SECTION */}
      <section ref={studioRef} className="relative min-h-screen w-full flex flex-col items-center justify-center py-24 bg-[#050505] z-30 overflow-hidden border-t border-white/5">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center">
          <div className="text-center mb-16">
            <h2 className="studio-title text-4xl sm:text-5xl md:text-[5.5vw] font-black uppercase italic leading-[0.85] text-white px-4">
              <div className="block">CHOOSE YOUR</div>
              <div className="block text-[#8888ff]">INFRASTRUCTURE.</div>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 w-full items-stretch">
            <div onMouseEnter={(e) => handleCardHover(e, false)} onMouseLeave={(e) => handleCardReset(e, false)} className="pricing-tier relative p-6 sm:p-8 border border-white/10 bg-white/[0.02] flex flex-col group overflow-hidden cursor-default transition-all">
              <div className="flex items-center justify-between mb-6 relative z-10"><span className="mono text-[8px] text-white/30 uppercase tracking-widest font-bold">Protocol.Starter</span></div>
              <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white mb-2 relative z-10">THE IGNITION</h3>
              <p className="text-[9px] sm:text-[10px] text-white/30 mb-6 mono uppercase tracking-tight relative z-10">For Instagram Startups</p>
              <div className="mb-8 relative z-10"><span className="text-3xl sm:text-4xl font-black italic text-[#8888ff] transition-colors duration-500">₹4,999</span><span className="mono text-[8px] text-white/20 ml-2 uppercase font-bold">/ Setup</span></div>
              <ul className="space-y-3.5 mb-10 flex-grow relative z-10">
                {['Bespoke Link-in-Bio', 'Instagram DM Automation', 'Basic Visual Aesthetic', 'Weekly Performance Logs'].map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[10px] sm:text-[11px] text-white/60 font-light"><Zap size={10} className="text-[#8888ff] mt-0.5 shrink-0" /><span>{feat}</span></li>
                ))}
              </ul>
              <button className="w-full py-3.5 border border-white/40 hover:bg-white text-white hover:text-black mono text-[9px] uppercase tracking-widest transition-all relative z-10 font-bold">Initialize Ignition</button>
            </div>
            <div onMouseEnter={(e) => handleCardHover(e, true)} onMouseLeave={(e) => handleCardReset(e, true)} className="pricing-tier relative p-6 sm:p-8 border border-[#8888ff]/40 bg-[#8888ff]/[0.04] flex flex-col shadow-[0_0_80px_rgba(136,136,255,0.1)] z-10 scale-[1.03] border-b-4 border-b-[#8888ff]/60 group overflow-hidden cursor-default transition-all">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#8888ff] text-[7px] font-black italic uppercase text-black tracking-[0.2em] rounded-sm transition-all whitespace-nowrap">RECOMMENDED</div>
              <div className="flex items-center justify-between mb-6 relative z-10"><Check className="text-[#8888ff]" size={16} /><span className="mono text-[8px] text-[#8888ff] uppercase tracking-widest font-bold">Protocol.Advanced</span></div>
              <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white mb-2 relative z-10">THE ASCENT</h3>
              <p className="text-[9px] sm:text-[10px] text-[#8888ff]/80 mb-6 mono uppercase tracking-tight relative z-10">For Established Influence</p>
              <div className="mb-8 relative z-10"><span className="text-3xl sm:text-4xl font-black italic text-white inline-block">₹14,999</span><span className="mono text-[8px] text-white/20 ml-2 uppercase font-bold">/ Build</span></div>
              <ul className="space-y-3.5 mb-10 flex-grow relative z-10">
                {['Full Digital Storefront', 'Advanced Motion Shaders', 'Cross-Platform Sync', 'Real-time Sales Analytics', 'Priority Lab Access'].map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[11px] sm:text-[12px] text-white font-medium"><Zap size={12} className="text-[#8888ff] mt-1 shrink-0" /><span>{feat}</span></li>
                ))}
              </ul>
              <button className="w-full py-4 bg-[#8888ff] hover:bg-white text-black font-black italic uppercase text-[9px] tracking-widest transition-all relative z-10">Begin The Ascent</button>
            </div>
            <div onMouseEnter={(e) => handleCardHover(e, false)} onMouseLeave={(e) => handleCardReset(e, false)} className="pricing-tier relative p-6 sm:p-8 border border-white/10 bg-white/[0.02] flex flex-col group overflow-hidden cursor-default transition-all">
              <div className="flex items-center justify-between mb-6 relative z-10"><span className="mono text-[8px] text-white/30 uppercase tracking-widest font-bold">Protocol.Enterprise</span></div>
              <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white mb-2 relative z-10">THE FLAGSHIP</h3>
              <p className="text-[9px] sm:text-[10px] text-white/30 mb-6 mono uppercase tracking-tight relative z-10">For Full Scale Companies</p>
              <div className="mb-8 relative z-10"><span className="text-3xl sm:text-4xl font-black italic text-white">CUSTOM</span><span className="mono text-[8px] text-white/20 ml-2 uppercase font-bold">/ Tailored</span></div>
              <ul className="space-y-3.5 mb-10 flex-grow relative z-10">
                {['White-Label Architecture', 'Dedicated Lab Engineer', 'Bespoke GLSL Shaders', 'Unlimited Growth Modules', '99.9% Core Stability'].map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[10px] sm:text-[11px] text-white/60 font-light"><Zap size={10} className="text-white/40 mt-0.5 shrink-0" /><span>{feat}</span></li>
                ))}
              </ul>
              <button className="w-full py-3.5 border border-white/40 hover:bg-white text-white hover:text-black mono text-[9px] uppercase tracking-widest transition-all relative z-10 font-bold">Initialize Ignition</button>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section ref={contactRef} className="relative min-h-screen w-full flex flex-col bg-[#050505] z-30 pt-32 md:pt-40 pb-20 border-t border-white/5 overflow-hidden">
        <div className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-16 w-full flex flex-col flex-grow relative z-10">
          <div className="grid grid-cols-12 gap-8 items-end mb-12 md:mb-16 lg:mb-24">
            <div className="col-span-12 lg:col-span-10">
              <span className="mono text-[8px] md:text-[9px] uppercase tracking-[0.5em] text-[#8888ff] mb-4 block font-bold">INITIATE_TRANSMISSION</span>
              <h2 className="contact-header text-4xl sm:text-5xl md:text-[5.5vw] font-black uppercase italic leading-[0.85] text-white select-none">
                <div className="block">READY TO</div>
                <div className="block text-[#8888ff]">DEPLOY?</div>
              </h2>
            </div>
          </div>

          <div className="contact-line h-[1px] w-full bg-white/5 mb-12 md:mb-16" />

          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 lg:col-span-7">
              <a href="mailto:kryven.in@gmail.com" className="group block relative perspective-1000 transition-all duration-500 max-w-full">
                <span className="mono text-[8px] md:text-[9px] uppercase tracking-[0.5em] text-white/20 mb-6 block group-hover:text-[#8888ff] transition-colors font-bold">DIRECT_CHANNEL</span>
                <div className="relative overflow-hidden">
                  <h3 className="text-[clamp(1.1rem,6vw,3rem)] font-black italic uppercase text-white transition-all duration-700 group-hover:text-[#8888ff] group-hover:translate-x-4 leading-none">
                    kryven.in@gmail.com
                  </h3>
                  <div className="absolute -bottom-2 left-0 w-0 h-[2px] bg-[#8888ff] transition-all duration-700 ease-expo group-hover:w-full" />
                </div>
              </a>
            </div>
          </div>

          <div className="mt-auto pt-16 md:pt-24 pb-8 flex flex-col md:flex-row items-end md:items-center justify-between gap-8 border-t border-white/5">
            <div className="flex flex-col gap-2">
              <p className="mono text-[7px] text-white/20 uppercase tracking-[0.2em] font-bold italic">© 2025 KRYVEN Laboratory. All rights reserved by the system.</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-6 md:gap-12 text-[9px] mono uppercase tracking-[0.3em] font-bold">
              <div className="flex gap-8 text-white/40">
                <a href="#" className="hover:text-[#8888ff] transition-colors">X / Twitter</a>
                <a href="#" className="hover:text-[#8888ff] transition-colors">Instagram</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
